import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import CryptoJS from "crypto-js";
import {
  UserIcon,
  MailIcon,
  LockClosedIcon,
  IdentificationIcon,
  EyeIcon,
  EyeOffIcon,
} from "@heroicons/react/outline";

// Import your existing actions/components
import { registerRequest, clearRegisterSuccess } from "../../redux/action";
import { RegisterButton } from "../Buttons";

// --- Reusable Input Component to Clean Up Code ---
const InputField = ({
  label,
  name,
  type = "text",
  formik,
  icon: Icon,
  placeholder,
  colSpan = "",
}) => {
  const hasError = formik.touched[name] && formik.errors[name];

  return (
    <div className={colSpan}>
      <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
        {label}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon
            className={`h-5 w-5 ${hasError ? "text-red-400" : "text-gray-400"}`}
          />
        </div>
        <input
          type={type}
          name={name}
          placeholder={placeholder}
          {...formik.getFieldProps(name)}
          className={`
            w-full pl-10 pr-4 py-3 rounded-xl border-2 transition-all outline-none
            ${
              hasError
                ? "border-red-300 bg-red-50 text-red-900 focus:border-red-500 placeholder-red-300"
                : "border-gray-200 bg-gray-50 focus:bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 text-gray-900"
            }
          `}
        />
      </div>
      {hasError && (
        <p className="mt-1 text-xs text-red-500 font-medium flex items-center gap-1 animate-pulse">
          {formik.errors[name]}
        </p>
      )}
    </div>
  );
};

// --- Password Component with Toggle ---
const PasswordField = ({ label, name, placeholder, formik }) => {
  const [show, setShow] = useState(false);
  const hasError = formik.touched[name] && formik.errors[name];

  return (
    <div>
      <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
        {label}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <LockClosedIcon
            className={`h-5 w-5 ${hasError ? "text-red-400" : "text-gray-400"}`}
          />
        </div>
        <input
          type={show ? "text" : "password"}
          name={name}
          placeholder={placeholder}
          {...formik.getFieldProps(name)}
          className={`
            w-full pl-10 pr-12 py-3 rounded-xl border-2 transition-all outline-none
            ${
              hasError
                ? "border-red-300 bg-red-50 text-red-900 focus:border-red-500"
                : "border-gray-200 bg-gray-50 focus:bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10"
            }
          `}
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
        >
          {show ? (
            <EyeOffIcon className="h-5 w-5" />
          ) : (
            <EyeIcon className="h-5 w-5" />
          )}
        </button>
      </div>
      {hasError && (
        <p className="mt-1 text-xs text-red-500 font-medium animate-pulse">
          {formik.errors[name]}
        </p>
      )}
    </div>
  );
};

// --- Main Component ---
export const RegisterForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const query = new URLSearchParams(useLocation().search);

  const { registerSuccess, registerError, registerLoading } = useSelector(
    (state) => ({
      registerSuccess: state.ajaxStatuses.success.register,
      registerError: state.ajaxStatuses.errors.register,
      registerLoading: state.loadingIndicator.registerLoading,
    })
  );

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      userName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: yup.object({
      firstName: yup
        .string()
        .required("Required")
        .matches(/^[a-zA-Z]+$/, "Alphabets only"),
      lastName: yup
        .string()
        .required("Required")
        .matches(/^[a-zA-Z]+$/, "Alphabets only"),
      userName: yup.string().required("Username is required"),
      email: yup.string().email("Invalid email").required("Email is required"),
      password: yup
        .string()
        .min(6, "Must be at least 6 characters")
        .required("Password is required"),
      confirmPassword: yup
        .string()
        .oneOf([yup.ref("password"), null], "Passwords do not match")
        .required("Confirm password is required"),
    }),
    onSubmit: (values) => {
      // Handle Encryption logic (Preserved from original)
      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(values.password),
        "my-secret-key@123"
      ).toString();

      const formData = { ...values, investmentDetail: encrypted };
      // Remove confirmPassword before sending if backend doesn't want it
      delete formData.confirmPassword;

      const refId = query.get("refId");
      dispatch(registerRequest({ formData, refId }));
    },
  });

  useEffect(() => {
    if (registerError) toast.error(registerError);
  }, [registerError]);

  useEffect(() => {
    if (registerSuccess) {
      toast.success("Account created successfully!");
      history.push("/dashboard");
      dispatch(clearRegisterSuccess());
    }
  }, [registerSuccess, history, dispatch]);

  return (
    <div className="max-w-xl mx-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50/50 px-4 py-8 text-center border-b border-gray-100">
          <h2 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">
            Create Account
          </h2>
          <p className="text-gray-500 text-sm">
            Join thousands of investors growing their wealth today.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={formik.handleSubmit} className="p-8 space-y-6">
          {/* Row 1: Names (Grid) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="First Name"
              name="firstName"
              placeholder="John"
              icon={UserIcon}
              formik={formik}
            />
            <InputField
              label="Last Name"
              name="lastName"
              placeholder="Doe"
              icon={UserIcon}
              formik={formik}
            />
          </div>

          {/* Row 2: Identity */}
          <InputField
            label="Username"
            name="userName"
            placeholder="johndoe123"
            icon={IdentificationIcon}
            formik={formik}
          />

          <InputField
            label="Email Address"
            name="email"
            type="email"
            placeholder="john@example.com"
            icon={MailIcon}
            formik={formik}
          />

          {/* Row 3: Security */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <PasswordField
              label="Password"
              name="password"
              placeholder="••••••"
              formik={formik}
            />
            <PasswordField
              label="Confirm Password"
              name="confirmPassword"
              placeholder="••••••"
              formik={formik}
            />
          </div>

          {/* Terms */}
          <div className="pt-2">
            <p className="text-xs text-gray-500 text-center leading-relaxed">
              By clicking "Create Account", you agree to our{" "}
              <Link
                to="/terms"
                className="text-primary-600 font-semibold hover:underline"
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                to="/privacy"
                className="text-primary-600 font-semibold hover:underline"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>

          {/* Action */}
          <RegisterButton
            loading={registerLoading}
            disable={!(formik.dirty && formik.isValid)}
            action={formik.handleSubmit}
            text="Create Account"
          />

          {/* Footer Link */}
          <div className="text-center pt-4">
            <p className="text-sm text-gray-600">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-primary-600 font-bold hover:text-primary-700 transition-colors"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
